import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, SlidersHorizontal, Loader2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BRANDS = ['Kent', 'Aquaguard', 'Livpure', 'Blue Star', 'Pureit', 'Universal'];

const SparePartsPage = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [allParts, setAllParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([{ id: 'all', name: 'All Parts', count: 0 }]);

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/spare-parts`);
        if (res.ok) {
          const data = await res.json();
          setAllParts(data);
          // Build categories from data
          const catMap = {};
          data.forEach(p => {
            catMap[p.category] = (catMap[p.category] || 0) + 1;
          });
          const cats = [{ id: 'all', name: 'All Parts', count: data.length }];
          Object.entries(catMap).forEach(([name, count]) => {
            cats.push({ id: name.toLowerCase().replace(/\s+/g, '-'), name, count });
          });
          setCategories(cats);
        }
      } catch (e) {
        console.error('Failed to fetch spare parts:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchParts();
  }, []);

  // Filter spare parts
  let filteredParts = [...allParts];

  if (selectedCategory !== 'all') {
    const categoryName = categories.find(c => c.id === selectedCategory)?.name;
    filteredParts = filteredParts.filter(p => p.category === categoryName);
  }

  if (selectedBrand !== 'all') {
    filteredParts = filteredParts.filter(p => (p.compatibility || []).includes(selectedBrand));
  }

  const handleAddToCart = (part) => {
    addToCart(part);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-lime-600 to-green-600 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">RO Spare Parts & Accessories</h1>
          <p className="text-xl text-lime-100">Genuine spare parts for all major RO brands with warranty</p>
        </div>
      </div>

      {loading ? (
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="w-10 h-10 text-lime-600 animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading spare parts...</p>
        </div>
      ) : allParts.length === 0 ? (
        <div className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon</h2>
          <p className="text-gray-600">Spare parts catalog is being updated. Please check back soon or contact us directly.</p>
        </div>
      ) : (
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <SlidersHorizontal className="text-lime-600" size={24} />
                  <h2 className="text-xl font-bold">Filters</h2>
                </div>

                {/* Category Filter */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Category</h3>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <label key={category.id} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="category"
                            value={category.id}
                            checked={selectedCategory === category.id}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="text-lime-600"
                          />
                          <span className="text-gray-700">{category.name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">{category.count}</Badge>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Brand Compatibility */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Compatible With</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="brand"
                        value="all"
                        checked={selectedBrand === 'all'}
                        onChange={(e) => setSelectedBrand(e.target.value)}
                        className="text-lime-600"
                      />
                      <span className="text-gray-700">All Brands</span>
                    </label>
                    {BRANDS.map(brand => (
                      <label key={brand} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="brand"
                          value={brand}
                          checked={selectedBrand === brand}
                          onChange={(e) => setSelectedBrand(e.target.value)}
                          className="text-lime-600"
                        />
                        <span className="text-gray-700">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                <Button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedBrand('all');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Clear All Filters
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Parts Grid */}
          <div className="lg:col-span-3">
            {/* Count */}
            <div className="mb-6">
              <p className="text-gray-700">
                Showing <span className="font-semibold">{filteredParts.length}</span> spare parts
              </p>
            </div>

            {/* Parts Grid */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredParts.map((part) => (
                <Card
                  key={part.id}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-lime-200"
                >
                  {/* Part Image */}
                  <div className="relative overflow-hidden bg-gray-100">
                    <img
                      src={part.image}
                      alt={part.name}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <Badge className="absolute top-4 right-4 bg-lime-500 hover:bg-lime-600 text-white font-semibold">
                      {part.category}
                    </Badge>
                    {part.originalPrice > part.price && (
                      <Badge className="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-white font-semibold">
                        {Math.round(((part.originalPrice - part.price) / part.originalPrice) * 100)}% OFF
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-5">
                    {/* Part Info */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-lime-700 transition-colors">
                      {part.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3">{part.description}</p>

                    {/* Compatibility */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Compatible with:</p>
                      <div className="flex flex-wrap gap-1">
                        {part.compatibility.slice(0, 3).map((brand, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {brand}
                          </Badge>
                        ))}
                        {part.compatibility.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{part.compatibility.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Lifespan */}
                    <p className="text-xs text-gray-600 mb-4">
                      <span className="font-semibold">Lifespan:</span> {part.lifespan}
                    </p>

                    {/* Price */}
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold text-lime-700">₹{part.price}</span>
                      {part.originalPrice > part.price && (
                        <span className="text-sm text-gray-400 line-through">₹{part.originalPrice}</span>
                      )}
                    </div>

                    {/* Stock Status & CTA */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate(`/spare-part/${part.id}`)}
                        variant="outline"
                        className="flex-1 py-5 rounded-lg font-semibold"
                        data-testid={`view-spare-${part.id}`}
                      >
                        <Eye size={18} className="mr-2" />
                        View Details
                      </Button>
                      {part.inStock ? (
                        <Button
                          onClick={() => handleAddToCart(part)}
                          className="flex-1 bg-gradient-to-r from-lime-600 to-green-600 hover:from-lime-700 hover:to-green-700 text-white font-semibold py-5 rounded-lg shadow-md hover:shadow-lg transition-all"
                        >
                          <ShoppingCart size={18} className="mr-2" />
                          Add to Cart
                        </Button>
                      ) : (
                        <Button disabled className="flex-1" variant="outline">
                          Out of Stock
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Empty State */}
            {filteredParts.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔧</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No spare parts found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters</p>
                <Button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedBrand('all');
                  }}
                  className="bg-lime-600 hover:bg-lime-700"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-lime-50 rounded-2xl p-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-lime-700 mb-2">100%</div>
              <div className="text-gray-900 font-semibold mb-1">Genuine Parts</div>
              <div className="text-sm text-gray-600">All authentic & certified</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-lime-700 mb-2">Free</div>
              <div className="text-gray-900 font-semibold mb-1">Installation Help</div>
              <div className="text-sm text-gray-600">Expert guidance included</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-lime-700 mb-2">Warranty</div>
              <div className="text-gray-900 font-semibold mb-1">On All Parts</div>
              <div className="text-sm text-gray-600">Manufacturer warranty</div>
            </div>
          </div>
        </div>
      </div>
      )}

      <Footer />
    </div>
  );
};

export default SparePartsPage;
